import { DAY_START_BLOCK } from "../../utils/constant"
import { getDayEnd, findNeighbors, isValidPlannerTarget, parseMachineAndBlock, getDropBlockRange } from "../../utils/prodPlanner"

/**
 * Attempts to insert a new tile into the planner with automatic conflict resolution
 * @param {Array} tiles - Current tiles in the planner
 * @param {Object} newTile - The tile to insert
 * @returns {Array|false} - Updated tiles array or false if insertion is impossible
 */
export function planInsert(tiles, newTile) {
  const machine = newTile.machine
  const dayEnd = getDayEnd(tiles)

  // Get all tiles on the same machine, sorted by start time
  const lane = tiles
    .filter((t) => t.machine === machine && t.objectId !== newTile.objectId)
    .sort((a, b) => a.start - b.start)

  const { prevIdx, nextIdx } = findNeighbors(lane, newTile.start)
  const prev = prevIdx >= 0 ? lane[prevIdx] : null
  const next = nextIdx < lane.length ? lane[nextIdx] : null

  const leftEdge = prev ? prev.end : DAY_START_BLOCK
  const rightEdge = next ? next.start : dayEnd
  const duration = newTile.end - newTile.start

  // Only insert directly if the proposed window fits entirely
  if (newTile.start >= leftEdge && newTile.end <= rightEdge) {
    return [...tiles.filter((t) => t.objectId !== newTile.objectId), newTile]
  }

  // Need to shift neighboring tiles to make space
  const gap = Math.max(0, rightEdge - leftEdge)
  const deficit = Math.max(0, duration - gap)
  const upNeed = Math.floor(deficit / 2)
  const downNeed = deficit - upNeed

  // Try balanced split first
  const split = tryPlan(tiles, newTile, lane, prevIdx, nextIdx, { up: upNeed, down: downNeed }, dayEnd)
  if (split) return split

  // Try shifting all up
  const upOnly = tryPlan(tiles, newTile, lane, prevIdx, nextIdx, { up: deficit, down: 0 }, dayEnd)
  if (upOnly) return upOnly

  // Try shifting all down
  const downOnly = tryPlan(tiles, newTile, lane, prevIdx, nextIdx, { up: 0, down: deficit }, dayEnd)
  if (downOnly) return downOnly

  return false
}

/**
 * Attempts to plan tile insertion with specific shift strategy
 */
function tryPlan(tiles, newTile, laneBase, prevIdx, nextIdx, plan, dayEnd) {
  const working = tiles.filter((t) => t.objectId !== newTile.objectId)
  const lane = laneBase.map((t) => ({ ...t }))

  // Shift tiles upward if needed
  if (plan.up > 0) {
    if (prevIdx >= 0) {
      if (!moveUp(lane, prevIdx, plan.up)) return false
    } else {
      const topGap = lane.length ? lane[0].start - DAY_START_BLOCK : Infinity
      if (topGap < plan.up) return false
      if (lane.length) lane[0] = { ...lane[0], start: lane[0].start - plan.up, end: lane[0].end - plan.up }
    }
  }

  // Shift tiles downward if needed
  if (plan.down > 0) {
    if (nextIdx < lane.length) {
      if (!moveDown(lane, nextIdx, plan.down, dayEnd)) return false
    } else {
      const bottomGap = lane.length ? dayEnd - lane[lane.length - 1].end : Infinity
      if (bottomGap < plan.down) return false
      if (lane.length) {
        const i = lane.length - 1
        lane[i] = { ...lane[i], start: lane[i].start + plan.down, end: lane[i].end + plan.down }
      }
    }
  }

  // Recompute window and clamp insert inside it
  const prev = prevIdx >= 0 ? lane[prevIdx] : null
  const next = nextIdx < lane.length ? lane[nextIdx] : null
  const leftEdge = prev ? prev.end : DAY_START_BLOCK
  const rightEdge = next ? next.start : dayEnd
  const duration = newTile.end - newTile.start

  const start = Math.max(leftEdge, Math.min(newTile.start, rightEdge - duration))
  const inserted = { ...newTile, start, end: start + duration }

  if (inserted.start < leftEdge || inserted.end > rightEdge) return false

  const others = working.filter((t) => t.machine !== newTile.machine)
  return [...others, ...lane, inserted]
}

/**
 * Recursively moves tiles upward to make space
 */
export const moveUp = (lane, idx, need) => {
  if (need <= 0) return true
  const tile = lane[idx]
  const above = idx > 0 ? lane[idx - 1] : null
  const slack = tile.start - (above ? above.end : DAY_START_BLOCK)

  if (slack >= need) {
    lane[idx] = { ...tile, start: tile.start - need, end: tile.end - need }
    return true
  }

  if (slack > 0) lane[idx] = { ...tile, start: tile.start - slack, end: tile.end - slack }

  const leftover = need - Math.max(slack, 0)
  if (!above) return false
  if (!moveUp(lane, idx - 1, leftover)) return false

  const t2 = lane[idx]
  lane[idx] = { ...t2, start: t2.start - leftover, end: t2.end - leftover }
  return true
}

/**
 * Recursively moves tiles downward to make space
 */
export const moveDown = (lane, idx, need, dayEnd) => {
  if (need <= 0) return true
  const tile = lane[idx]
  const below = idx < lane.length - 1 ? lane[idx + 1] : null
  const slack = (below ? below.start : dayEnd) - tile.end

  if (slack >= need) {
    lane[idx] = { ...tile, start: tile.start + need, end: tile.end + need }
    return true
  }

  if (slack > 0) lane[idx] = { ...tile, start: tile.start + slack, end: tile.end + slack }

  const leftover = need - Math.max(slack, 0)
  if (!below) return false
  if (!moveDown(lane, idx + 1, leftover, dayEnd)) return false

  const t2 = lane[idx]
  lane[idx] = { ...t2, start: t2.start + leftover, end: t2.end + leftover }
  return true
}

/**
 * Pushes tiles upward using available slack
 */
export const pushUp = (lane, chain, amount) => {
  let remaining = amount
  // Consume slack nearest to the insertion first (prev, then above, etc.)
  for (let k = 0; k < chain.length; k++) {
    const i = chain[k]
    const above = i > 0 ? lane[i - 1] : null
    const gap = lane[i].start - (above ? above.end : DAY_START_BLOCK)
    const delta = Math.min(remaining, gap)
    if (delta > 0) {
      lane[i] = {
        ...lane[i],
        start: lane[i].start - delta,
        end: lane[i].end - delta,
      }
      remaining -= delta
      if (remaining === 0) break
    }
  }
  return remaining === 0
}

/**
 * Pushes tiles downward using available slack
 */
export const pushDown = (lane, chain, amount, dayEnd) => {
  let remaining = amount
  // Consume slack nearest to the insertion first (next, then below, etc.)
  for (let k = 0; k < chain.length; k++) {
    const i = chain[k]
    const below = i < lane.length - 1 ? lane[i + 1] : null
    const gap = (below ? below.start : dayEnd) - lane[i].end
    const delta = Math.min(remaining, gap)
    if (delta > 0) {
      lane[i] = {
        ...lane[i],
        start: lane[i].start + delta,
        end: lane[i].end + delta,
      }
      remaining -= delta
      if (remaining === 0) break
    }
  }
  return remaining === 0
}

/**
 * Handles tile resize with automatic conflict resolution
 * @param {Array} tiles - Current tiles
 * @param {string} resizedId - ID of the tile being resized
 * @param {Object} changes - New start/end values
 * @returns {Array} - Updated tiles array
 */
export function planResize(tiles, resizedId, changes) {
  const idx = tiles.findIndex((t) => t.objectId === resizedId)
  if (idx === -1) return tiles

  const old = tiles[idx]
  const newStart = changes.start != null ? changes.start : old.start
  const newEnd = changes.end != null ? changes.end : old.end

  // Validate: start < end
  if (newStart >= newEnd) return tiles

  const dayEnd = getDayEnd(tiles)
  
  // Get all tiles on the same machine, sorted by position
  const lane = tiles
    .filter((t) => t.machine === old.machine)
    .sort((a, b) => a.start - b.start)
  
  // Find the index of the resized tile in the sorted lane
  const resizedIdx = lane.findIndex((t) => t.objectId === resizedId)
  
  // Determine direction and amount of expansion
  const isExpandingDown = changes.end != null && changes.end > old.end
  const isExpandingUp = changes.start != null && changes.start < old.start
  
  if (!isExpandingDown && !isExpandingUp) {
    // Shrinking only - just update
    return tiles.map((t) => t.objectId === resizedId ? { ...t, start: newStart, end: newEnd } : t)
  }
  
  // Create a working copy of the lane
  const workingLane = lane.map((t) => ({ ...t }))
  
  if (isExpandingUp) {
    const shift = old.start - newStart
    const tileAbove = resizedIdx > 0 ? workingLane[resizedIdx - 1] : null
    
    // Calculate available gap above the resized tile
    const gapAbove = old.start - (tileAbove ? tileAbove.end : DAY_START_BLOCK)
    
    // Check if new position would go below day start
    if (newStart < DAY_START_BLOCK) {
      return tiles // Can't resize - would go below day start
    }
    
    // If there's enough gap, just expand into it
    if (gapAbove >= shift) {
      workingLane[resizedIdx] = { ...workingLane[resizedIdx], start: newStart, end: newEnd }
    } else {
      // Not enough gap - need to push tiles above
      const pushAmount = shift - gapAbove
      
      // First, push tiles above
      if (!pushTilesUp(workingLane, resizedIdx - 1, pushAmount, DAY_START_BLOCK)) {
        return tiles // Can't resize - not enough space
      }
      
      // Then update the resized tile
      workingLane[resizedIdx] = { ...workingLane[resizedIdx], start: newStart, end: newEnd }
    }
  } else if (isExpandingDown) {
    const shift = newEnd - old.end
    const tileBelow = resizedIdx < workingLane.length - 1 ? workingLane[resizedIdx + 1] : null
    
    // Calculate available gap below the resized tile
    const gapBelow = (tileBelow ? tileBelow.start : dayEnd) - old.end
    
    // Check if new position would exceed day end
    if (newEnd > dayEnd) {
      return tiles // Can't resize - would exceed day end
    }
    
    // If there's enough gap, just expand into it
    if (gapBelow >= shift) {
      workingLane[resizedIdx] = { ...workingLane[resizedIdx], start: newStart, end: newEnd }
    } else {
      // Not enough gap - need to push tiles below
      const pushAmount = shift - gapBelow
      
      // First, push tiles below
      if (!pushTilesDown(workingLane, resizedIdx + 1, pushAmount, dayEnd)) {
        return tiles // Can't resize - not enough space
      }
      
      // Then update the resized tile
      workingLane[resizedIdx] = { ...workingLane[resizedIdx], start: newStart, end: newEnd }
    }
  }
  
  // If we got here, the resize is valid - apply changes
  return tiles.map((t) => {
    const updated = workingLane.find((wt) => wt.objectId === t.objectId)
    return updated || t
  })
}

/**
 * Recursively pushes tiles upward
 */
function pushTilesUp(lane, startIdx, shift, dayStart) {
  if (shift <= 0) return true // Nothing to push
  if (startIdx < 0) return true // No tiles above
  
  const tile = lane[startIdx]
  const tileAbove = startIdx > 0 ? lane[startIdx - 1] : null
  
  // Calculate available gap above this tile
  const gapAbove = tile.start - (tileAbove ? tileAbove.end : dayStart)
  
  // If there's enough gap, just move this tile up without pushing further
  if (gapAbove >= shift) {
    lane[startIdx] = { ...tile, start: tile.start - shift, end: tile.end - shift }
    return true
  }
  
  // Not enough gap - we need to use the gap and push tiles above for the rest
  const remainingShift = shift - gapAbove
  
  // Calculate new position for this tile (using all available gap)
  const newStart = tile.start - shift
  const newEnd = tile.end - shift
  
  // Check if it would go below day start
  if (newStart < dayStart) {
    return false
  }
  
  // We need to push the tile above by the remaining amount
  if (tileAbove) {
    // Recursively try to push tiles above
    if (!pushTilesUp(lane, startIdx - 1, remainingShift, dayStart)) {
      return false // Can't push tiles above
    }
  } else {
    // No tile above but still need to push - impossible
    return false
  }
  
  // Update this tile's position
  lane[startIdx] = { ...tile, start: newStart, end: newEnd }
  return true
}

/**
 * Recursively pushes tiles downward
 */
function pushTilesDown(lane, startIdx, shift, dayEnd) {
  if (shift <= 0) return true // Nothing to push
  if (startIdx >= lane.length) return true // No tiles below
  
  const tile = lane[startIdx]
  const tileBelow = startIdx < lane.length - 1 ? lane[startIdx + 1] : null
  
  // Calculate available gap below this tile
  const gapBelow = (tileBelow ? tileBelow.start : dayEnd) - tile.end
  
  // If there's enough gap, just move this tile down without pushing further
  if (gapBelow >= shift) {
    lane[startIdx] = { ...tile, start: tile.start + shift, end: tile.end + shift }
    return true
  }
  
  // Not enough gap - we need to use the gap and push tiles below for the rest
  const remainingShift = shift - gapBelow
  
  // Calculate new position for this tile (using all available gap)
  const newStart = tile.start + shift
  const newEnd = tile.end + shift
  
  // Check if it would exceed day end
  if (newEnd > dayEnd) {
    return false
  }
  
  // We need to push the tile below by the remaining amount
  if (tileBelow) {
    // Recursively try to push tiles below
    if (!pushTilesDown(lane, startIdx + 1, remainingShift, dayEnd)) {
      return false // Can't push tiles below
    }
  } else {
    // No tile below but still need to push - impossible
    return false
  }
  
  // Update this tile's position
  lane[startIdx] = { ...tile, start: newStart, end: newEnd }
  return true
}

/**
 * Collects chain of tiles that need to move down
 */
export const collectChainDown = (lane, nextIdx, need, dayEnd) => {
  if (need <= 0) return []

  // If there is no tile below, only the boundary provides slack
  if (nextIdx >= lane.length) {
    const bottomGap = lane.length ? dayEnd - lane[lane.length - 1].end : Infinity
    return bottomGap >= need ? [] : null
  }

  const chain = []
  let remaining = need

  // Walk from the immediate neighbor downward, accumulating slack
  for (let i = nextIdx; i < lane.length; i++) {
    const below = i < lane.length - 1 ? lane[i + 1] : null
    const gap = (below ? below.start : dayEnd) - lane[i].end

    chain.push(i)
    remaining -= gap

    if (remaining <= 0) break
  }

  return remaining <= 0 ? chain : null
}

/**
 * Collects chain of tiles that need to move up
 */
export const collectChainUp = (lane, prevIdx, need) => {
  if (need <= 0) return []

  // No tile above: only the top boundary gives slack
  if (prevIdx < 0) {
    const topGap = lane.length ? lane[0].start - DAY_START_BLOCK : Infinity
    return topGap >= need ? [] : null
  }

  const chain = []
  let remaining = need

  // Walk upward, accumulating available gaps above each tile
  for (let i = prevIdx; i >= 0; i--) {
    const above = i > 0 ? lane[i - 1] : null
    const gap = lane[i].start - (above ? above.end : DAY_START_BLOCK)

    chain.push(i) // Include this tile in the chain to move
    remaining -= gap // Gap we can consume by moving it up

    if (remaining <= 0) break
  }

  // Enough space? return indices; otherwise impossible
  return remaining <= 0 ? chain : null
}

/**
 * Wrapper for resize with automatic shift resolution
 */
export const resizeAndShiftTiles = (tiles, tileId, changes) => {
  return planResize(tiles, tileId, changes) || tiles
}

/**
 * Creates a resize handler for tile edges
 */
export const createResizeHandler = ({ direction, tile, setIsResizing, onResize }) => (e) => {
  e.stopPropagation()
  setIsResizing(true)

  const startY = e.clientY
  const originalStart = tile.start
  const originalEnd = tile.end

  const onMouseMove = (moveEvent) => {
    const deltaY = moveEvent.clientY - startY
    const blocks = Math.round(deltaY / 25)

    let newStart = originalStart
    let newEnd = originalEnd

    if (direction === "top") {
      newStart = Math.max(0, originalStart + blocks)
      if (newStart >= newEnd) newStart = newEnd - 1
    } else {
      newEnd = Math.max(originalStart + 1, originalEnd + blocks)
    }

    onResize(tile.objectId, { start: newStart, end: newEnd })
  }

  const onMouseUp = () => {
    setIsResizing(false)
    window.removeEventListener("mousemove", onMouseMove)
    window.removeEventListener("mouseup", onMouseUp)
  }

  window.addEventListener("mousemove", onMouseMove)
  window.addEventListener("mouseup", onMouseUp)
}

/**
 * Handles drop event for tiles in the planner
 */
export const handleDrop = (tiles, setTiles, setPreviewTiles) => (event) => {
  const { active, over } = event
  setPreviewTiles([])

  if (!over) return

  // Handle drop to unassigned panel
  if (over.id === "unassigned") {
    setTiles((prev) => {
      // Find the tile being moved to unassigned
      const movedTile = prev.find((t) => t.objectId === active.id)
      if (!movedTile) return prev
      
      // Create the unassigned version of the tile
      const unassignedTile = {
        ...movedTile,
        machine: null,
        start: null,
        end: null,
        duration: null,
      }
      
      // Remove the tile from its current position and add it to the top
      const otherTiles = prev.filter((t) => t.objectId !== active.id)
      return [unassignedTile, ...otherTiles]
    })
    return
  }

  if (!isValidPlannerTarget(over.id)) return

  const { machine, blockIndex } = parseMachineAndBlock(over.id)
  if (!machine || Number.isNaN(blockIndex)) return

  setTiles((prev) => {
    const dragged = prev.find((t) => t.objectId === active.id)
    if (!dragged) return prev

    const { startBlock, endBlock } = getDropBlockRange(dragged, blockIndex)
    const dropped = { 
      ...dragged, 
      machine, 
      start: startBlock, 
      end: endBlock,
    }

    // Call engine with the list WITHOUT the dragged tile
    const base = prev.filter((t) => t.objectId !== active.id)
    const result = planInsert(base, dropped)

    // If infeasible, keep previous; if feasible, commit
    return Array.isArray(result) ? result : prev
  })
}

/**
 * Handles drag move event and shows preview of affected tiles
 */
export const handleDrag = (tiles, setPreviewTiles) => (event) => {
  const { active, over } = event

  if (!over || !isValidPlannerTarget(over.id)) {
    setPreviewTiles([])
    return
  }

  const { machine, blockIndex } = parseMachineAndBlock(over.id)
  if (!machine || Number.isNaN(blockIndex)) {
    setPreviewTiles([])
    return
  }

  const dragged = tiles.find((t) => t.objectId === active.id)
  if (!dragged) {
    setPreviewTiles([])
    return
  }

  // Calculate where the tile would be positioned based on mouse location
  const { startBlock, endBlock } = getDropBlockRange(dragged, blockIndex)
  
  const ghost = { ...dragged, machine, start: startBlock, end: endBlock }

  const base = tiles.filter((t) => t.objectId !== active.id)
  const after = planInsert(base, ghost)
  if (!Array.isArray(after)) {
    setPreviewTiles([])
    return
  }

  // Diff: show only affected tiles (excluding the ghost)
  const byId = new Map(base.map((t) => [t.objectId, t]))
  const preview = after
    .filter((t) => t.objectId !== ghost.objectId)
    .filter((t) => {
      const orig = byId.get(t.objectId)
      return (
        !orig ||
        orig.start !== t.start ||
        orig.end !== t.end ||
        orig.machine !== t.machine
      )
    })
    .map((t) => ({ ...t, isPreview: true }))

  setPreviewTiles(preview)
}
